import { useEffect, useRef, useState } from 'react'
import type { Control, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { format } from 'date-fns'
import { Pencil, Calendar, ShieldCheck, CircleDollarSign, TrendingDown } from 'lucide-react'
import { entityActionButtonClusterClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import detailStyles from '@/features/reimbursements/styles/reimbursementDetail.module.css'

import { ReimbursementEndDateOverrideDialog } from '../dialogs/ReimbursementEndDateOverrideDialog'
import type {
  ResidentApplicationData,
  ResidentDefaultFormValues,
  ResidentDetailResponse,
} from '../../types/resident'
import {
  FIRST_MONTH_NOT_PAID_MONTH_VALUES,
  formatFirstMonthNotPaidMonthLabel,
  getCalculationTabSnapshot,
} from '../../utils/residentPayload'
import { AuditBlock } from './ResidentCalculationAuditBlocks'
import { ResidentDateField } from '../ResidentDateField'
import {
  CLAIM_STATUSES,
  FIRST_MONTH_YEAR_START,
  REIMBURSEMENT_END_DATE_OVERRIDE_ROLES,
  RESIDENT_CALCULATION_ACTION_BUTTON_CLASS,
  RESIDENT_STATUS_DATE_EDIT_ROLES,
} from '../../constants/residentConstants'
import { applyPartialPaymentToLossAdjustment, canShowCalculationFinancials } from '../../utils/residentValidation'
import { getDailyRentMonthDaysCount } from '../../utils/residentCalculationClientFallback'
import { formatNoteWhen } from '@/lib/utils/noteFormatting'

function yearOptions(): number[] {
  const end = new Date().getFullYear()
  return Array.from({ length: end - FIRST_MONTH_YEAR_START + 1 }, (_, i) => FIRST_MONTH_YEAR_START + i)
}

function fmt(value: unknown): string {
  if (value === undefined || value === null || value === '') return '$0.00'
  const num = Number(String(value).replace(/[^0-9.-]/g, ''))
  if (!Number.isFinite(num)) return '$0.00'
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function asText(value: unknown): string {
  if (value === undefined || value === null || value === '') return '-'
  return String(value)
}

/**
 * Full Calculation tab body — matches Angular `resident-edit-new` calculation
 * `*ngSwitchCase="'calculation'"`: editable dates/amounts, read-only
 * `defaultProcessData` figures (API snapshot merged like Angular), Liberty overrides, actions.
 * The same `react-hook-form` instance as Reimbursement tab so Save posts one payload.
 */
export function ResidentCalculationTabForm({
  control,
  register,
  setValue,
  values,
  errors,
  disabled,
  roleName,
  residentDetail,
  applicationData,
  isLibertyRole,
  canConfigurePayment = false,
  onConfigurePayment,
  onDeny,
  onReopen,
  calculationSaveDisabled,
  userId,
  applicationId,
  currentUserDisplayName,
  onReimbursementEndDateOverrideSaved,
  onRentalStatusDateUserChange,
  publicNotesAndMessages,
  onFirstMonthChange,
  isOverrideCleared = false,
}: {
  control: Control<ResidentDefaultFormValues>
  register: UseFormRegister<ResidentDefaultFormValues>
  setValue: UseFormSetValue<ResidentDefaultFormValues>
  values: ResidentDefaultFormValues
  errors: FieldErrors<ResidentDefaultFormValues>
  disabled: boolean
  roleName: string
  residentDetail: ResidentDetailResponse | null
  applicationData: ResidentApplicationData | null
  isLibertyRole: boolean
  canConfigurePayment?: boolean
  onConfigurePayment?: () => void
  onDeny?: () => void
  onReopen?: () => void
  /** Parent form submit Save; aligned with Configure Payment / Deny / Re-open like Angular. */
  calculationSaveDisabled: boolean
  userId?: string
  applicationId?: string | number
  currentUserDisplayName?: string
  onReimbursementEndDateOverrideSaved?: (
    savedDate: string,
    reason: string,
    response?: ResidentDetailResponse,
  ) => void | Promise<void>
  onRentalStatusDateUserChange?: (value: string) => void
  publicNotesAndMessages?: Array<Record<string, unknown>> | null
  onFirstMonthChange?: () => void
  isOverrideCleared?: boolean
}) {
  const [showEndDateOverrideModal, setShowEndDateOverrideModal] = useState(false)
  // Angular component fields (not re-derived from API on every reload after save):
  //   reimbursementEndDateOverrideUpdatedBy / reimbursementEndDateOverrideUpdatedAt
  const [libertyOverrideUpdatedBy, setLibertyOverrideUpdatedBy] = useState('')
  const [libertyOverrideUpdatedAt, setLibertyOverrideUpdatedAt] = useState('')
  const preserveOverrideAuditRef = useRef(false)
  const residentDefault = residentDetail?.resident_default
  const calc = getCalculationTabSnapshot(residentDetail, applicationData, values)
  const canShowFinancials = canShowCalculationFinancials({
    residentDetail,
    rentalProgress: values.rental_progress,
  })
  const claimStatus = String(residentDefault?.claim_status || '')
  const isDenied = claimStatus === 'DENIED'
  const reviewDecision = String(residentDefault?.review_decision || '')
  const rentalStatus = values.rental_status
  const showMoveOut = rentalStatus && !['NOT_STARTED', 'ACTIVE', 'CLOSED'].includes(rentalStatus)
  const canEditStatusDate = (RESIDENT_STATUS_DATE_EDIT_ROLES as readonly string[]).includes(roleName)
  const isExternalInsurance = roleName === 'EXTERNAL_INSURANCE'
  const firstMonthReadOnly =
    isLibertyRole && !['ROLE_ADMIN', 'ROLE_LIBERTY_DEFAULTS'].includes(roleName)
  const dailyDaysCount = getDailyRentMonthDaysCount(
    residentDetail,
    String(calc.max_movein_date || values.adjust_max_movein_date || ''),
  )
  const dailyRateLabel =
    isLibertyRole && dailyDaysCount
      ? `Daily Reimbursement Rate (${dailyDaysCount} Days)`
      : 'Daily Reimbursement Rate'

  const applyPartialPaymentCopy = () => {
    const next = applyPartialPaymentToLossAdjustment({
      lossAdjustment: values.loss_adjustment,
      partialPaymentAmount: values.partial_payment_amount,
    })
    if (next.loss_adjustment !== undefined) {
      setValue('loss_adjustment', next.loss_adjustment, { shouldDirty: true })
    }
  }

  const showConfigure =
    isLibertyRole &&
    canConfigurePayment &&
    ((!residentDetail?.payment_review_log?.length &&
      values.rental_progress !== 'COMPLETED' &&
      !['DENIED', 'UNDER_REVIEW'].includes(claimStatus)) ||
      (!!residentDetail?.payment_review_log?.length &&
        (['L1 Recalculated', 'L2 Recalculated'].includes(reviewDecision) || claimStatus === 'RE_OPENED')))
  const showDeny =
    isLibertyRole &&
    !['DENIED', 'UNDER_REVIEW', 'LIBERTY_REVIEW', 'EXTERNAL_REVIEW'].includes(claimStatus)
  const showReopen = isLibertyRole && claimStatus === 'DENIED'

  const showCalculationSave =
    !isExternalInsurance &&
    !!rentalStatus &&
    !['NOT_STARTED', 'ACTIVE'].includes(String(rentalStatus))
  const showLibertyActions =
    isLibertyRole && (showConfigure || showDeny || showReopen)
  const showCalculationActionRow = showLibertyActions || showCalculationSave
  const canOverrideReimbursementEndDate = (REIMBURSEMENT_END_DATE_OVERRIDE_ROLES as readonly string[]).includes(
    roleName,
  )
  const rdRecord = residentDefault as Record<string, unknown> | undefined
  const reimbursementOverrideSource = String(
    rdRecord?.reimbursement_end_date_override_source ?? '',
  )
  const showOverrideAttribution = Boolean(reimbursementOverrideSource) && !isOverrideCleared
  const existingOverrideReason = String(rdRecord?.reimbursement_end_date_override_reason ?? '')
  const defaultRecordKey = String(
    rdRecord?.fk_resident_default_id ?? rdRecord?.id ?? userId ?? '',
  )

  // Angular setReimbursementEndDateOverrideAudit() — hydrate when opening a record.
  // After Save Override, keep the local moment() stamp (preserveOverrideAuditRef) so a
  // silent reload cannot replace day 3 with the UTC API wall-clock day 2.
  const lastRecordKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (lastRecordKeyRef.current !== defaultRecordKey) {
      lastRecordKeyRef.current = defaultRecordKey
      preserveOverrideAuditRef.current = false
    }
    if (preserveOverrideAuditRef.current) return
    if (!reimbursementOverrideSource) {
      setLibertyOverrideUpdatedBy('')
      setLibertyOverrideUpdatedAt('')
      return
    }
    setLibertyOverrideUpdatedBy(
      String(rdRecord?.reimbursement_end_date_override_updated_by ?? 'System'),
    )
    setLibertyOverrideUpdatedAt(formatNoteWhen(rdRecord?.reimbursement_end_date_override_at))
  }, [
    defaultRecordKey,
    reimbursementOverrideSource,
    rdRecord?.reimbursement_end_date_override_updated_by,
    rdRecord?.reimbursement_end_date_override_at,
  ])

  const fieldClass = (name: keyof ResidentDefaultFormValues) =>
    `mt-1 h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-button focus:ring-2 focus:ring-blue-100 ${
      errors[name] ? 'border-red-500' : 'border-slate-200'
    } ${disabled ? 'cursor-not-allowed opacity-70' : ''}`

  const textClass = (name: keyof ResidentDefaultFormValues) =>
    `mt-1 min-h-[52px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-button ${
      disabled ? 'cursor-not-allowed opacity-70' : ''
    } ${errors[name] ? 'border-red-500' : ''}`

  return (
    <section
      className="space-y-4"
      data-testid="resident-calculation-form"
    >
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="m-0 text-xl font-bold text-slate-900">Reimbursement Calculation Information</h2>
        {claimStatus ? (
          <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
            {CLAIM_STATUSES[claimStatus as keyof typeof CLAIM_STATUSES] || claimStatus}
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr] lg:items-stretch">
        <div className={cn(detailStyles.section, 'flex h-full flex-col')}>
          <h2 className={detailStyles.sectionTitle}>
            <span className={detailStyles.sectionTitleIcon} aria-hidden="true">
              <Calendar className="h-4 w-4" />
            </span>
            Key Dates &amp; Lease
          </h2>

          <div
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
            data-testid="resident-calculation-form-dates-row"
          >
            <label className="text-sm font-medium text-slate-700">
              Reimbursement Request Date
              <ResidentDateField
                control={control}
                name="rental_status_date"
                disabled={!canEditStatusDate || disabled}
                error={!!errors.rental_status_date}
                onUserChange={onRentalStatusDateUserChange}
              />
              <AuditBlock field="rental_status_date" residentDetail={residentDetail} publicNotesAndMessages={publicNotesAndMessages} />
            </label>

            {showMoveOut ? (
              <label className="text-sm font-medium text-slate-700">
                Move-Out Date
                <ResidentDateField control={control} name="moveout_date" disabled={disabled} error={!!errors.moveout_date} />
              </label>
            ) : null}

            <label
              className={`text-sm font-medium text-slate-700 ${
                isExternalInsurance ? 'pointer-events-none opacity-70' : ''
              }`}
            >
              New Resident Lease Start Date
              <ResidentDateField
                control={control}
                name="new_resident_lease_start_date"
                disabled={disabled || isExternalInsurance}
                error={!!errors.new_resident_lease_start_date}
              />
              <AuditBlock field="new_resident_lease_start_date" residentDetail={residentDetail} publicNotesAndMessages={publicNotesAndMessages} />
            </label>

            <label className="text-sm font-medium text-slate-700">
              New Resident Move-In Date
              <ResidentDateField control={control} name="movein_date" disabled={disabled} error={!!errors.movein_date} />
              <AuditBlock field="movein_date" residentDetail={residentDetail} publicNotesAndMessages={publicNotesAndMessages} />
            </label>
          </div>

          <div
            className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2"
            data-testid="resident-calculation-form-first-month-row"
          >
            <div data-testid="resident-calculation-form-first-month-not-paid">
              <span className="text-sm font-medium text-slate-700">First Month Rent Not Paid</span>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <select
                  disabled={firstMonthReadOnly || disabled}
                  className={fieldClass('first_month_not_paid_month').replace('mt-1 ', '')}
                  data-testid="resident-calculation-form-first-month-not-paid-month"
                  {...register('first_month_not_paid_month')}
                  onChange={(e) => {
                    void register('first_month_not_paid_month').onChange(e)
                    onFirstMonthChange?.()
                  }}
                >
                  <option value="">Select month</option>
                  {FIRST_MONTH_NOT_PAID_MONTH_VALUES.map((month) => (
                    <option key={month} value={month}>
                      {formatFirstMonthNotPaidMonthLabel(month)}
                    </option>
                  ))}
                </select>
                <select
                  disabled={firstMonthReadOnly || disabled}
                  className={fieldClass('first_month_not_paid_year').replace('mt-1 ', '')}
                  data-testid="resident-calculation-form-first-month-not-paid-year"
                  {...register('first_month_not_paid_year')}
                  onChange={(e) => {
                    void register('first_month_not_paid_year').onChange(e)
                    onFirstMonthChange?.()
                  }}
                >
                  <option value="">Select year</option>
                  {yearOptions().map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <AuditBlock field="first_month_not_paid" residentDetail={residentDetail} publicNotesAndMessages={publicNotesAndMessages} />
            </div>

            <label className="text-sm font-medium text-slate-700">
              Lease Age
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="Months"
                className={`${fieldClass('lease_month')} cursor-default bg-slate-50`}
                data-testid="resident-calculation-form-lease-month-input"
                {...register('lease_month')}
                value={String(calc.lease_month ?? values.lease_month ?? '')}
                readOnly
              />
            </label>
          </div>

          <div
            className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] lg:items-start"
            data-testid="resident-calculation-form-reimbursement-window-row"
          >
            {canShowFinancials ? (
              <label className="min-w-0 text-sm font-medium text-slate-700">
                Reimbursement Start Date
                <ResidentDateField
                  control={control}
                  name="adjusted_notice"
                  disabled={!canEditStatusDate || disabled}
                  error={!!errors.adjusted_notice}
                />
                <AuditBlock field="adjusted_notice" residentDetail={residentDetail} publicNotesAndMessages={publicNotesAndMessages} />
              </label>
            ) : null}

            <div className="min-w-0 text-sm font-medium text-slate-700">
              <span className="block">Reimbursement End Date</span>
              <div className="mt-1 flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <ResidentDateField
                    control={control}
                    name="adjust_max_movein_date"
                    disabled={disabled}
                    readOnly
                    error={!!errors.adjust_max_movein_date}
                    dataTestId="resident-calculation-form-adjust-max-movein-date"
                  />
                </div>
                {canOverrideReimbursementEndDate && userId ? (
                  <button
                    type="button"
                    title="Override Reimbursement End Date"
                    onClick={() => setShowEndDateOverrideModal(true)}
                    data-testid="resident-calculation-form-reimbursement-end-date-edit"
                    className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[7px] border border-[#1597ff] text-[#1597ff] transition hover:bg-[#e3f2fb]"
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </button>
                ) : null}
              </div>
              <AuditBlock field="adjust_max_movein_date" residentDetail={residentDetail} publicNotesAndMessages={publicNotesAndMessages} />
              {showOverrideAttribution ? (
                <p
                  className="mb-0 mt-1 text-xs leading-snug text-[#778aa4]"
                  data-testid="resident-calculation-liberty-override-audit"
                >
                  <span className="font-semibold text-slate-700">Liberty Override</span>
                  <br />
                  <span className="italic">
                    {libertyOverrideUpdatedBy || 'System'}
                    {libertyOverrideUpdatedAt ? ` — ${libertyOverrideUpdatedAt}` : ''}
                  </span>
                </p>
              ) : null}
            </div>

            <div className="min-w-0 lg:pt-8" data-testid="resident-calculation-form-max-reason-wrap">
              <div className="flex min-h-10 items-center gap-2 whitespace-nowrap">
                <span className="shrink-0 text-sm font-medium text-slate-700">Max Reason:</span>
                <span
                  className="text-sm font-medium text-slate-800"
                  data-testid="resident-calculation-form-max-reason"
                >
                  {asText(calc.max_reason)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          className={cn(detailStyles.section, 'flex h-full flex-col')}
          data-testid="resident-calculation-form-review-overrides"
        >
          <h2 className={detailStyles.sectionTitle}>
            <span className={detailStyles.sectionTitleIcon} aria-hidden="true">
              <ShieldCheck className="h-4 w-4" />
            </span>
            Review &amp; Overrides
          </h2>
          <div className="flex flex-1 flex-col space-y-4">
            {isLibertyRole ? (
              <div data-testid="resident-calculation-form-liberty-overrides">
                <label className="block text-sm font-medium text-slate-700">
                  Reimbursement Status
                  <select
                    className={fieldClass('claim_status')}
                    data-testid="resident-calculation-form-claim-status-select"
                    {...register('claim_status')}
                  >
                    <option value="">All Reimbursement Statuses</option>
                    {Object.entries(CLAIM_STATUSES).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                {residentDefault?.claim_status_updated_by ? (
                  <p className="mt-2 text-xs text-slate-500">
                    {String(residentDefault.claim_status_updated_by)}
                    {residentDefault.claim_update_date ? ` — ${String(residentDefault.claim_update_date)}` : ''}
                  </p>
                ) : null}
              </div>
            ) : null}

            {canShowFinancials ? (
              <label className="flex flex-1 flex-col text-sm font-medium text-slate-700">
                Adjusted Reason
                <textarea
                  className={cn(textClass('adjusted_reason'), 'flex-1 resize-none')}
                  data-testid="resident-calculation-form-adjusted-reason-textarea"
                  {...register('adjusted_reason')}
                />
              </label>
            ) : null}
          </div>
        </div>
      </div>

      {canShowFinancials ? (
        <>
          <div
            className="rounded-xl border border-sky-100 bg-sky-50/70 p-4 sm:p-5"
            data-testid="resident-calculation-form-summary-row-wrap"
          >
            <h2 className="m-0 mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sky-900">
              <CircleDollarSign className="h-4 w-4 text-sky-700" aria-hidden="true" />
              Reimbursement Summary
            </h2>
            <div
              className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5"
              data-testid="resident-calculation-form-summary-row"
            >
              <SummaryStat label="Reimbursement Rate" value={fmt(calc.rent_amount ?? applicationData?.rent_amount)} />
              <SummaryStat label="Reimbursement Period (Months)" value={asText(calc.notice_month_diff)} />
              <SummaryStat label={dailyRateLabel} value={fmt(calc.daily_rent)} />
              <SummaryStat label="Gross Reimbursement" value={fmt(calc.loss_rent)} />
              <SummaryStat
                className="col-span-2 sm:col-span-1 xl:col-span-1"
                label={`Max Reimbursement End Date${calc.max_reason ? ` : ${calc.max_reason}` : ''}`}
                value={formatSummaryDate(values.adjust_max_movein_date || calc.max_movein_date)}
              />
            </div>
          </div>

          <div className={detailStyles.section} data-testid="resident-calculation-form-loss-adjustments">
            <h2 className={detailStyles.sectionTitle}>
              <span className={detailStyles.sectionTitleIcon} aria-hidden="true">
                <TrendingDown className="h-4 w-4" />
              </span>
              Loss Adjustments
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              <label className="text-sm font-medium text-slate-700">
                Unused Security Deposit
                <input
                  className={fieldClass('unused_security_deposit')}
                  data-testid="resident-calculation-form-unused-security-deposit-input"
                  {...register('unused_security_deposit')}
                />
              </label>
              <label className="text-sm font-medium text-slate-700">
                Partial Payments
                <input
                  className={fieldClass('partial_payment_amount')}
                  data-testid="resident-calculation-form-partial-payment-amount-input"
                  {...register('partial_payment_amount')}
                  onBlur={(event) => {
                    register('partial_payment_amount').onBlur(event)
                    applyPartialPaymentCopy()
                  }}
                />
              </label>
              <label className="text-sm font-medium text-slate-700">
                Other Adjustments
                <input
                  className={fieldClass('loss_adjustment')}
                  data-testid="resident-calculation-form-loss-adjustment-input"
                  {...register('loss_adjustment')}
                />
              </label>
            </div>
          </div>

          <div
            className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5"
            data-testid="resident-calculation-form-totals-row-wrap"
          >
            <div
              className="grid grid-cols-1 gap-4 sm:grid-cols-3"
              data-testid="resident-calculation-form-totals-row"
            >
              <SummaryStat
                label="Net Reimbursement Payment"
                value={fmt(calc.net_due)}
                icon={<CircleDollarSign className="h-5 w-5 text-emerald-600" aria-hidden="true" />}
              />
              {isDenied ? <SummaryStat label="Net Denial Amount" value={fmt(calc.net_amount ?? values.net_amount)} /> : null}
              <SummaryStat label="Lost LR Reimbursement" value={fmt(calc.lost_revenue)} />
              <SummaryStat label="Months Lost for Not Reporting" value={asText(calc.months_lost)} />
            </div>
          </div>
        </>
      ) : null}

      {showCalculationActionRow ? (
        <div
          className="mt-6 flex flex-col items-stretch gap-2.5 border-t border-slate-100 pt-4 sm:flex-row sm:flex-wrap sm:items-center"
          data-testid="resident-calculation-form-actions"
        >
          <div className={entityActionButtonClusterClass}>
          {showLibertyActions ? (
            <>
              {showConfigure ? (
                <button
                  type="button"
                  onClick={onConfigurePayment}
                  data-testid="resident-calculation-form-configure-payment-button"
                  className={RESIDENT_CALCULATION_ACTION_BUTTON_CLASS}
                >
                  Configure Payment
                </button>
              ) : null}
              {showDeny ? (
                <>
                  <button
                    type="button"
                    onClick={onDeny}
                    data-testid="resident-calculation-form-deny-button"
                    className={RESIDENT_CALCULATION_ACTION_BUTTON_CLASS}
                  >
                    Deny
                  </button>
                  {showCalculationSave ? (
                    <button
                      type="submit"
                      disabled={calculationSaveDisabled}
                      data-testid="resident-calculation-form-save-button"
                      className={RESIDENT_CALCULATION_ACTION_BUTTON_CLASS}
                    >
                      Save
                    </button>
                  ) : null}
                </>
              ) : null}
              {showReopen ? (
                <button
                  type="button"
                  onClick={onReopen}
                  data-testid="resident-calculation-form-reopen-button"
                  className={RESIDENT_CALCULATION_ACTION_BUTTON_CLASS}
                >
                  Re-open
                </button>
              ) : null}
              {showCalculationSave && !showDeny ? (
                <button
                  type="submit"
                  disabled={calculationSaveDisabled}
                  data-testid="resident-calculation-form-save-button"
                  className={RESIDENT_CALCULATION_ACTION_BUTTON_CLASS}
                >
                  Save
                </button>
              ) : null}
            </>
          ) : null}
          {!showLibertyActions && showCalculationSave ? (
            <button
              type="submit"
              disabled={calculationSaveDisabled}
              data-testid="resident-calculation-form-save-button"
              className={RESIDENT_CALCULATION_ACTION_BUTTON_CLASS}
            >
              Save
            </button>
          ) : null}
          </div>
        </div>
      ) : null}

      {userId ? (
        <ReimbursementEndDateOverrideDialog
          isOpen={showEndDateOverrideModal}
          onClose={() => setShowEndDateOverrideModal(false)}
          userId={userId}
          applicationId={applicationId}
          formValues={values}
          systemCalculatedEndDate={calc.max_movein_date ? String(calc.max_movein_date) : ''}
          currentEndDate={values.adjust_max_movein_date}
          contractEndDate={applicationData?.contract_end_date}
          existingReason={existingOverrideReason || undefined}
          currentUserDisplayName={currentUserDisplayName || 'System'}
          onSaved={async (savedDate, reason, response) => {
            // Angular: reimbursementEndDateOverrideUpdatedAt = moment().format('YYYY-MM-DD hh:mm:ss A')
            preserveOverrideAuditRef.current = true
            setLibertyOverrideUpdatedBy(currentUserDisplayName || 'System')
            setLibertyOverrideUpdatedAt(format(new Date(), 'yyyy-MM-dd hh:mm:ss a'))
            setValue('adjust_max_movein_date', savedDate, { shouldDirty: true })
            await onReimbursementEndDateOverrideSaved?.(savedDate, reason, response)
          }}
        />
      ) : null}
    </section>
  )
}

/** Display max / summary dates as MM-DD-YYYY (calendar date, no UTC shift). */
function formatSummaryDate(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  const raw = String(value).trim()
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (ymd) return `${ymd[2]}-${ymd[3]}-${ymd[1]}`
  return raw.slice(0, 10) || '—'
}

function SummaryStat({
  label,
  value,
  icon,
  className,
}: {
  label: string
  value: string
  icon?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <p
        className="m-0 text-[11px] font-bold uppercase leading-snug tracking-wider text-slate-500 break-words"
        title={label}
      >
        {label}
      </p>
      <p className="m-0 mt-1 flex items-center gap-1.5 text-lg font-bold text-slate-900">
        {icon}
        {value}
      </p>
    </div>
  )
}
